import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Badge, Button, Col, Form, Row, Table as BootstrapTable } from "react-bootstrap";

import CsLineIcons from "../../../cs-line-icons";
import { loading, send as pageInfo } from "../../../actions/page";
import { pack as sendAlert } from "../../../actions/alert";
import { listDestinations, saveDestination, deleteDestination } from "../../../actions/hooks";

const defaultDestination = () => ({
    uuid: null,
    name: "",
    url: "",
    supports: "FS",
    headers: "[]",
    retries: 10,
    delay: 5000,
    maximum: 300000
});

const numberOrDefault = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

class HookSettings extends React.Component {
    state = {
        items: [],
        selected: defaultDestination(),
        secret: ""
    };

    componentDidMount = async () => {
        await this.props.pageInfo({ title: "Hook Settings", breadcrumb: "Hooks" });
        await this.loadData();
    };

    loadData = async () => {
        this.props.loading(true);
        try {
            const items = await this.props.listDestinations();
            this.setState((current) => ({
                items,
                selected: current.selected?.uuid
                    ? items.find((item) => item.uuid === current.selected.uuid) || defaultDestination()
                    : (items[0] || defaultDestination()),
                secret: ""
            }));
        } finally {
            this.props.loading(false);
        }
    };

    select = (destination) => {
        this.setState({ selected: { ...defaultDestination(), ...(destination || {}) }, secret: "" });
    };

    onChange = (field, value) => {
        this.setState((current) => ({
            selected: {
                ...current.selected,
                [field]: value
            }
        }));
    };

    payload = () => {
        const selected = this.state.selected || defaultDestination();
        const payload = {
            ...selected,
            retries: numberOrDefault(selected.retries, 10),
            delay: numberOrDefault(selected.delay, 5000),
            maximum: numberOrDefault(selected.maximum, 300000)
        };

        if (!payload.uuid) delete payload.uuid;
        if (this.state.secret) payload.secret = this.state.secret;
        return payload;
    };

    save = async () => {
        this.props.loading(true);
        try {
            const result = await this.props.saveDestination(this.payload());
            if (result?.success) {
                this.props.sendAlert(["Hook destination saved."], "success");
                await this.loadData();
                return;
            }
            this.props.sendAlert(["Hook destination was not saved."], "danger");
        } finally {
            this.props.loading(false);
        }
    };

    deleteSelected = async () => {
        const uuid = this.state.selected?.uuid;
        if (!uuid) {
            this.select(defaultDestination());
            return;
        }
        if (!window.confirm("Delete this hook destination?")) return;

        this.props.loading(true);
        try {
            const result = await this.props.deleteDestination(uuid);
            if (result?.success) {
                this.props.sendAlert(["Hook destination deleted."], "success");
                await this.loadData();
                return;
            }
            this.props.sendAlert(["Hook destination was not deleted."], "danger");
        } finally {
            this.props.loading(false);
        }
    };

    renderRows = () => {
        if (!this.state.items.length) {
            return (
                <tr>
                    <td colSpan="4" className="text-muted">No hook destinations.</td>
                </tr>
            );
        }

        return this.state.items.map((item) => (
            <tr key={item.uuid} className={item.uuid === this.state.selected?.uuid ? "table-active" : ""}>
                <td>
                    <button type="button" className="btn btn-link p-0 text-start" onClick={() => this.select(item)}>
                        {item.name}
                    </button>
                </td>
                <td><Badge bg="secondary">{item.supports || "FS"}</Badge></td>
                <td>{item.retries ?? 10}</td>
                <td className="text-truncate" style={{ maxWidth: 360 }}>{item.url}</td>
            </tr>
        ));
    };

    render() {
        const selected = this.state.selected || defaultDestination();

        return (
            <Col xs="12">
                <Row className="g-4">
                    <Col lg="7">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="small-title mb-0">Destinations</h2>
                            <Button variant="outline-primary" size="sm" onClick={() => this.select(defaultDestination())}>
                                <CsLineIcons icon="plus" size="15" className="me-2" />
                                New
                            </Button>
                        </div>
                        <BootstrapTable responsive hover className="align-middle">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Supports</th>
                                    <th>Retries</th>
                                    <th>URL</th>
                                </tr>
                            </thead>
                            <tbody>{this.renderRows()}</tbody>
                        </BootstrapTable>
                    </Col>
                    <Col lg="5">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="small-title mb-0">Destination</h2>
                            <div className="d-flex gap-2">
                                <Button variant="outline-primary" size="sm" onClick={this.save}>
                                    <CsLineIcons icon="save" size="15" className="me-2" />
                                    Save
                                </Button>
                                <Button variant={selected.uuid ? "outline-danger" : "outline-secondary"} size="sm" onClick={this.deleteSelected}>
                                    <CsLineIcons icon={selected.uuid ? "bin" : "close"} size="15" className="me-2" />
                                    {selected.uuid ? "Delete" : "Clear"}
                                </Button>
                            </div>
                        </div>
                        <Row className="g-3">
                            <Col xs="12">
                                <Form.Label>Name</Form.Label>
                                <Form.Control value={selected.name || ""} onChange={(e) => this.onChange("name", e.target.value)} />
                            </Col>
                            <Col xs="12">
                                <Form.Label>URL</Form.Label>
                                <Form.Control value={selected.url || ""} onChange={(e) => this.onChange("url", e.target.value)} />
                            </Col>
                            <Col sm="6">
                                <Form.Label>Supports</Form.Label>
                                <Form.Control value={selected.supports || "FS"} onChange={(e) => this.onChange("supports", e.target.value)} />
                            </Col>
                            <Col sm="6">
                                <Form.Label>Headers</Form.Label>
                                <Form.Control value={selected.headers || "[]"} onChange={(e) => this.onChange("headers", e.target.value)} />
                            </Col>
                            <Col sm="4">
                                <Form.Label>Retries</Form.Label>
                                <Form.Control type="number" value={selected.retries ?? 10} onChange={(e) => this.onChange("retries", e.target.value)} />
                            </Col>
                            <Col sm="4">
                                <Form.Label>Delay</Form.Label>
                                <Form.Control type="number" value={selected.delay ?? 5000} onChange={(e) => this.onChange("delay", e.target.value)} />
                            </Col>
                            <Col sm="4">
                                <Form.Label>Maximum</Form.Label>
                                <Form.Control type="number" value={selected.maximum ?? 300000} onChange={(e) => this.onChange("maximum", e.target.value)} />
                            </Col>
                            <Col xs="12">
                                <Form.Label>Secret</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={this.state.secret}
                                    placeholder="Leave blank to keep existing secret."
                                    onChange={(e) => this.setState({ secret: e.target.value })}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>
        );
    }
}

HookSettings.propTypes = {
    pageInfo: PropTypes.func.isRequired,
    loading: PropTypes.func.isRequired,
    sendAlert: PropTypes.func.isRequired,
    listDestinations: PropTypes.func.isRequired,
    saveDestination: PropTypes.func.isRequired,
    deleteDestination: PropTypes.func.isRequired
};

export default connect(null, {
    pageInfo,
    loading,
    sendAlert,
    listDestinations,
    saveDestination,
    deleteDestination
})(HookSettings);
